import { Injectable } from '@angular/core';

/**
 * Fuente única de verdad de la marca según el dominio.
 *
 * Regla: es Tourbo si el hostname contiene 'tourbo' o es localhost/127.0.0.1.
 * Cualquier otro dominio (incluido home.jump.flaptek.com) es Flaptek.
 *
 * Reemplaza la vieja lógica `isJump` del header.
 */
@Injectable({
  providedIn: 'root',
})
export class BrandService {
  readonly isFlaptek: boolean;
  readonly brandName: 'Flaptek' | 'Tourbo';
  readonly logoPath: string;
  readonly faviconPath: string;

  constructor() {
    const host = (
      typeof window !== 'undefined' ? window.location.hostname : ''
    ).toLowerCase();
    const isTourbo =
      host.includes('tourbo') || host === 'localhost' || host === '127.0.0.1';

    // Override de preview para QA local (no afecta la regla real de dominio):
    // ?brand=flaptek | ?brand=tourbo fuerza la marca y se persiste; ?brand=auto
    // limpia el override y vuelve a decidir por hostname.
    const override = this.resolveOverride();
    this.isFlaptek = override ?? !isTourbo;
    this.brandName = this.isFlaptek ? 'Flaptek' : 'Tourbo';
    this.logoPath = this.isFlaptek
      ? 'assets/img/logo-flaptek.svg'
      : 'assets/img/logo-tourbo-header.svg';
    this.faviconPath = this.isFlaptek
      ? 'assets/img/favicons/favicon-flaptek.ico'
      : 'favicon.ico';
  }

  /**
   * Lee el override de preview (query param ?brand y localStorage). Devuelve
   * true (Flaptek), false (Tourbo) o null (sin override -> decide el hostname).
   */
  private resolveOverride(): boolean | null {
    if (typeof window === 'undefined') {
      return null;
    }
    const param = new URLSearchParams(window.location.search)
      .get('brand')
      ?.toLowerCase();
    try {
      if (param === 'flaptek' || param === 'tourbo') {
        localStorage.setItem('brand-override', param);
      } else if (param === 'auto' || param === 'clear') {
        localStorage.removeItem('brand-override');
      }
      const stored = localStorage.getItem('brand-override');
      if (stored === 'flaptek') return true;
      if (stored === 'tourbo') return false;
    } catch {
      // localStorage no disponible: ignoramos el override
    }
    return null;
  }

  /**
   * Aplica la marca al documento de forma idempotente: clase de tema en <html>,
   * favicon y título base. El mismo trabajo lo hace el snippet inline de
   * index.html antes del primer paint; esto lo deja como fuente de verdad Angular.
   */
  applyBranding(): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.classList.toggle('theme-flaptek', this.isFlaptek);

    if (this.isFlaptek) {
      const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
      if (link) {
        link.href = this.faviconPath;
      }
      document.title = this.brandName;
    }
  }
}
